import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { selectClientsList } from 'src/store/selectors/clientSelectors';
import { getClientFullName } from 'src/utils/common';
import { LOAD_CLIENTS, loadAllClients } from 'src/store/actions/clientActions';
import DialogTransition from 'src/components/DialogTransition';
import { ASSIGN_SANTANDER_CLABE } from 'src/store/actions/santanderClabeActions';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from './styles';

const AssignClientDialog = ({ open, handleClose, onSubmit }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const loading = useSelector(
    isLoadingSelector([ASSIGN_SANTANDER_CLABE.action])
  );
  const isLoadingClients = useSelector(
    isLoadingSelector([LOAD_CLIENTS.action])
  );
  const clients = useSelector(selectClientsList);

  const initialState = useMemo(() => makeInitialState(), []);
  const resetRef = useRef();

  useEffect(() => {
    if (open) {
      dispatch(loadAllClients({ withoutClabe: true }));
    }
  }, [open, dispatch]);

  const handleSubmitAssignment = useCallback(
    (values, { resetForm }) => {
      if (values.clientId) {
        onSubmit(values.clientId, handleClose);
        resetForm(initialState);
      }
    },
    [onSubmit, handleClose, initialState, clients.length, isLoadingClients]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  const clientOptions = useMemo(
    () =>
      clients.map(clientOption => ({
        label: `${clientOption.id} - ${getClientFullName(clientOption)}`,
        value: clientOption
      })),
    [clients]
  );

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      keepMounted
      onClose={handleClose}
      classes={{ paper: classes.dialogRoot }}
      TransitionComponent={DialogTransition}
    >
      <DialogTitle
        className={classes.dialogTitle}
        id="assign-client-dialog-title"
      >
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={handleClose}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.titleTypography} variant="h2">
          {t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.ASSIGN_CLABE')}
        </Typography>
      </DialogTitle>
      <DialogContent
        className={classes.dialogContent}
        sx={{
          height: '11rem',
          width: '30rem!important',
          padding: '0px',
          margin: '0px'
        }}
      >
        <Formik
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={handleSubmitAssignment}
          enableReinitialize
        >
          {({
            errors,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue
          }) => {
            resetRef.current = () => resetForm();
            return (
              <Form onSubmit={handleSubmit}>
                <Box
                  className={classes.autocomplete}
                  width="100%"
                  sx={{ margin: "0.5rem 0 0 0" }}
                >
                  <Autocomplete
                    options={clientOptions}
                    value={
                        values.clientId
                          ? clientOptions.find(
                              option =>
                                option.value.id === values.clientId
                            )
                          : null
                      }
                    onChange={(event, newValue) => {
                      setFieldValue(
                        'clientId',
                        newValue ? newValue.value.id : null
                      );
                    }}
                    getOptionLabel={option => option.label || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value?.value?.id
                    }
                    label={t(
                      'ADMINISTRATOR_VIEW.SANTANDER_CLABES.SELECT_CLIENT'
                    )}
                    error={Boolean(touched.clientId && errors.clientId)}
                    helperText={
                      touched.clientId && errors.clientId
                        ? t(errors.clientId)
                        : ''
                    }
                    disabled={
                      loading || isLoadingClients || clients.length === 0
                    }
                    isLoading={isLoadingClients}
                    name="clientId"
                  />
                </Box>

                {!isLoadingClients && clients.length === 0 && (
                  <Typography
                    className={classes.noClientsTypography}
                    variant="caption"
                    color="text.secondary"
                  >
                    {t(
                      'ADMINISTRATOR_VIEW.SANTANDER_CLABES.NO_CLIENTS_AVAILABLE'
                    )}
                  </Typography>
                )}

                <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    className={classes.confirmButton}
                    type="submit"
                    disabled={loading || isLoadingClients || clients.length === 0}
                    startIcon={
                      loading ? <CircularProgress size={20} color="inherit" /> : null
                    }
                    variant="outlined"
                  >
                    {loading ? t('COMMON.LOADING') : t('COMMON.ASSIGN')}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

AssignClientDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default AssignClientDialog;
